import { Text, darkColorTokens, lightColorTokens } from "@carbon6/c6-frontend-design";
import type { ColorTokens } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { hatcheryTextModes } from "@/app/theme";
import { GnbMenu } from "@/components/base";
import { PageBase, type PageBaseContext, type PageBaseProps } from "@/page/base/PageBase";
import styles from "./DebugPage.module.css";

const debugTabs = {
  ariaLabel: "Debug sections",
  defaultValue: "theme",
  items: [{ value: "theme", label: "Theme" }],
} as const;

type ColorTokenGroup = {
  label: string;
  rows: readonly ColorTokenRow[];
};

type ColorTokenRow = {
  name: string;
  cssVar: string;
  light: string;
  dark: string;
};

export function DebugPage() {
  return (
    <DebugPageView pageTitle="Debug" currentMenu={GnbMenu.Debug} scrollable tabs={debugTabs} />
  );
}

class DebugPageView extends PageBase<PageBaseProps> {
  protected override renderContent(context: PageBaseContext): ReactNode {
    if (context.currentTab !== "theme") {
      return null;
    }

    return <ThemeDebugPanel />;
  }
}

function ThemeDebugPanel(): ReactNode {
  return (
    <section className={styles.shell} aria-label="테마 색상 토큰">
      <Text variant={hatcheryTextModes["body-normal"]} className={styles.intro}>
        c6-frontend-design light and dark color tokens with rendered swatches.
      </Text>

      {colorTokenGroups.map((group) => (
        <section key={group.label} className={styles.group} aria-labelledby={group.label}>
          <div className={styles.groupHeader}>
            <Text
              variant={hatcheryTextModes["title-bold"]}
              className={styles.groupTitle}
              id={group.label}
            >
              {group.label}
            </Text>
            <Text variant={hatcheryTextModes["caption-bold"]} className={styles.groupCount}>
              {group.rows.length} tokens
            </Text>
          </div>
          <div className={styles.tokenTable}>
            <div className={styles.tokenHeader}>
              <div className={styles.cell}>토큰</div>
              <div className={styles.cell}>밝은 테마</div>
              <div className={styles.cell}>어두운 테마</div>
            </div>
            {group.rows.map((row) => (
              <div key={row.cssVar} className={styles.tokenRow}>
                <div className={styles.cell} data-label="토큰">
                  <Text variant={hatcheryTextModes["body-bold"]} className={styles.tokenName}>
                    {row.name}
                  </Text>
                  <span className={styles.cssVar}>{row.cssVar}</span>
                </div>
                <ColorTokenCell label="밝은 테마" value={row.light} />
                <ColorTokenCell label="어두운 테마" value={row.dark} />
              </div>
            ))}
          </div>
        </section>
      ))}
    </section>
  );
}

function ColorTokenCell({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className={styles.cell} data-label={label}>
      <div className={styles.colorSample}>
        <span className={styles.swatchSurface} aria-hidden="true">
          <span className={styles.swatch} style={{ background: value }} />
        </span>
        <div>
          <div className={styles.colorValue}>{value}</div>
          <div className={styles.colorHex}>{formatColorSummary(value)}</div>
        </div>
      </div>
    </div>
  );
}

function createTokenRow(name: string, cssVar: string, light: string, dark: string): ColorTokenRow {
  return {
    name,
    cssVar,
    light,
    dark,
  };
}

function createCssVar(path: string): string {
  return `--c6-color-${path
    .replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`)
    .replace(/\./g, "-")}`;
}

function colorRow(name: string, pick: (tokens: ColorTokens) => string): ColorTokenRow {
  return createTokenRow(name, createCssVar(name), pick(lightColorTokens), pick(darkColorTokens));
}

const colorTokenGroups: readonly ColorTokenGroup[] = [
  {
    label: "Background",
    rows: [
      colorRow("bg.canvas", (tokens) => tokens.bg.canvas),
      colorRow("bg.surface", (tokens) => tokens.bg.surface),
      colorRow("bg.surfaceSunken", (tokens) => tokens.bg.surfaceSunken),
      colorRow("bg.surfaceRaised", (tokens) => tokens.bg.surfaceRaised),
      colorRow("bg.surfaceRaisedHigh", (tokens) => tokens.bg.surfaceRaisedHigh),
      colorRow("bg.surfaceOverlay", (tokens) => tokens.bg.surfaceOverlay),
      colorRow("bg.inverse", (tokens) => tokens.bg.inverse),
    ],
  },
  {
    label: "Control",
    rows: [
      colorRow("bg.control.primary", (tokens) => tokens.bg.control.primary),
      colorRow("bg.control.primaryHover", (tokens) => tokens.bg.control.primaryHover),
      colorRow("bg.control.primaryActive", (tokens) => tokens.bg.control.primaryActive),
      colorRow("bg.control.danger", (tokens) => tokens.bg.control.danger),
      colorRow("bg.control.dangerHover", (tokens) => tokens.bg.control.dangerHover),
      colorRow("bg.control.dangerActive", (tokens) => tokens.bg.control.dangerActive),
      colorRow("bg.control.warning", (tokens) => tokens.bg.control.warning),
      colorRow("bg.control.warningHover", (tokens) => tokens.bg.control.warningHover),
      colorRow("bg.control.warningActive", (tokens) => tokens.bg.control.warningActive),
      colorRow("bg.control.disabled", (tokens) => tokens.bg.control.disabled),
    ],
  },
  {
    label: "Text",
    rows: [
      colorRow("text.muted", (tokens) => tokens.text.muted),
      colorRow("text.subtle", (tokens) => tokens.text.subtle),
      colorRow("text.default", (tokens) => tokens.text.default),
      colorRow("text.disabled", (tokens) => tokens.text.disabled),
      colorRow("text.accent", (tokens) => tokens.text.accent),
      colorRow("text.warning", (tokens) => tokens.text.warning),
      colorRow("text.danger", (tokens) => tokens.text.danger),
      colorRow("text.inverse", (tokens) => tokens.text.inverse),
      colorRow("text.onControl", (tokens) => tokens.text.onControl),
    ],
  },
  {
    label: "Border",
    rows: [
      colorRow("border.subtle", (tokens) => tokens.border.subtle),
      colorRow("border.default", (tokens) => tokens.border.default),
      colorRow("border.focus", (tokens) => tokens.border.focus),
      colorRow("border.selected", (tokens) => tokens.border.selected),
      colorRow("border.selectedHover", (tokens) => tokens.border.selectedHover),
      colorRow("border.selectedActive", (tokens) => tokens.border.selectedActive),
      colorRow("border.danger", (tokens) => tokens.border.danger),
      colorRow("border.disabled", (tokens) => tokens.border.disabled),
    ],
  },
  {
    label: "Scrim",
    rows: [colorRow("scrim.default", (tokens) => tokens.scrim.default)],
  },
  {
    // 코어 14키만 표시한다. status.{device,job,inference,dataCapture,capacity} 도메인 그룹의 값은
    // 모두 이 코어의 별칭이다. 그래서 표시하면 같은 색 38줄이 표를 채운다. 도메인 어휘 확인은 c6
    // Storybook `Foundations/Theme/Status Core` 에서 한다.
    label: "Status",
    rows: [
      colorRow("status.success", (tokens) => tokens.status.success),
      colorRow("status.ready", (tokens) => tokens.status.ready),
      colorRow("status.running", (tokens) => tokens.status.running),
      colorRow("status.completed", (tokens) => tokens.status.completed),
      colorRow("status.info", (tokens) => tokens.status.info),
      colorRow("status.paused", (tokens) => tokens.status.paused),
      colorRow("status.attention", (tokens) => tokens.status.attention),
      colorRow("status.warning", (tokens) => tokens.status.warning),
      colorRow("status.error", (tokens) => tokens.status.error),
      colorRow("status.cancelling", (tokens) => tokens.status.cancelling),
      colorRow("status.recording", (tokens) => tokens.status.recording),
      colorRow("status.exhausted", (tokens) => tokens.status.exhausted),
      colorRow("status.checking", (tokens) => tokens.status.checking),
      colorRow("status.muted", (tokens) => tokens.status.muted),
    ],
  },
  {
    label: "Brand",
    rows: [colorRow("brand.primary", (tokens) => tokens.brand.primary)],
  },
];

function formatColorSummary(value: string): string {
  if (value.startsWith("#")) {
    return value.toUpperCase();
  }

  const rgbMatch = value.match(
    /^rgba?\(\s*(\d+)\s*(?:,\s*|\s+)(\d+)\s*(?:,\s*|\s+)(\d+)(?:\s*(?:\/|,)\s*([\d.]+))?\s*\)$/,
  );
  if (rgbMatch == null) {
    return value;
  }

  const [, red, green, blue, alpha] = rgbMatch;
  const hex = `#${toHex(red)}${toHex(green)}${toHex(blue)}`;
  if (alpha == null) {
    return hex;
  }

  return `${hex} @ ${formatAlpha(alpha)}`;
}

function toHex(value: string): string {
  return Number(value).toString(16).padStart(2, "0").toUpperCase();
}

function formatAlpha(alpha: string): string {
  return `${Math.round(Number(alpha) * 100)}%`;
}
